import React from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { imgUrl } from '../../utils/api-request';
import { formatBytes } from '../DiskUsage';
import { isAdmin } from '../../utils/admin';
import { getDeletionState, TONE_BADGE_CLASS } from '../../utils/contentDeletionState';
import { formatDateBy, formatTime } from '../../utils/dates';
import { identityKeyStatus, keyStatusOf, pickRowForLocation } from '../../utils/contentGroups';
import './ContentGroupCard.scss';

const LOCATION_CHIP = {
    BAC_server: { icon: 'fa-hard-drive', label: 'BAC', name: 'BAC server' },
    mediablock: { icon: 'fa-cube', label: 'MB', name: 'Mediablock' },
};

const STATUS_TONE = { valid: 'text-success', upcoming: 'text-warning', expired: 'text-danger' };

// What a key's window means today, in the words a projectionist uses: an expired key is dated by
// how long ago it lapsed, a live one by when it stops working, a future one by when it starts.
const keyWindowLabel = (notValidBefore, notValidAfter) => {
    switch (keyStatusOf(notValidBefore, notValidAfter)) {
        case 'expired': return `Expired ${moment(notValidAfter).fromNow()}`;
        case 'upcoming': return `Valid from ${formatDateBy(notValidBefore, 'dd MMM yyyy')}`;
        case 'valid': return `Valid until ${formatDateBy(notValidAfter, 'dd MMM yyyy')}`;
        default: return 'No validity dates';
    }
};

// One chip per location a member exists on; clicking opens that copy's modal without triggering
// the line. Tooltip carries the arrival date so the line itself stays short.
const LocationChips = ({ member, toggleContentModal }) => (
    <span className="cg-chips">
        {member.locations.map(location => {
            const row = member.byLocation[location];
            const chip = LOCATION_CHIP[location];
            return (
                <button
                    type="button"
                    key={location}
                    className="cg-chip"
                    title={`On ${chip.name}${row.onMachine ? ` since ${new Date(row.onMachine).toLocaleString()}` : ''}`}
                    onClick={event => {
                        event.stopPropagation();
                        toggleContentModal(row)();
                    }}
                >
                    <span className={`fas ${chip.icon}`} /> {chip.label}
                </button>
            );
        })}
    </span>
);

const KeyLine = ({ keyItem, preferredLocation, showTitle, mediablockSerialNumber, toggleContentModal }) => {
    const row = pickRowForLocation(keyItem.byLocation, preferredLocation);
    const serialMismatch = row.serialNumber && mediablockSerialNumber && row.serialNumber !== mediablockSerialNumber;
    // A key cut for another Mediablock is useless whatever its dates say.
    const tone = serialMismatch ? 'text-danger' : STATUS_TONE[keyStatusOf(keyItem.notValidBefore, keyItem.notValidAfter)];

    return (
        <div className="cg-line cg-key" onClick={toggleContentModal(row)}>
            <span className={`fas fa-key cg-line-icon ${tone || ''}`} />
            <span className="cg-line-title text-truncate" title={keyItem.contentTitleText}>
                {showTitle ? keyItem.contentTitleText : `Key ${keyItem.uuid.split('-')[0]}`}
                {keyItem.rows.length > 1 && (
                    <span className="cg-copies" title={`Stored ${keyItem.rows.length} times`}>×{keyItem.rows.length}</span>
                )}
                {serialMismatch && (
                    <span className="ms-2 badge bg-danger text-white">
                        <span className="fas fa-exclamation-circle me-1" />Serial Mismatch
                    </span>
                )}
            </span>
            <span className="cg-line-meta">{keyWindowLabel(keyItem.notValidBefore, keyItem.notValidAfter)}</span>
            <LocationChips member={keyItem} toggleContentModal={toggleContentModal} />
        </div>
    );
};

const PackageLine = ({ identity, nested, preferredLocation, admin, mediablockSerialNumber, toggleContentModal }) => {
    const row = pickRowForLocation(identity.byLocation, preferredLocation);
    const onMediablock = identity.locations.includes('mediablock');
    const bacRow = identity.byLocation.BAC_server;
    const deletion = getDeletionState(identity.byLocation.mediablock);
    const ingestFailed = bacRow?.ingestFailedAt && !onMediablock;
    const ingesting = bacRow?.ingestRequestedAt && !ingestFailed && !onMediablock;
    const tone = STATUS_TONE[identityKeyStatus(identity, admin)];

    return (
        <div className={`cg-package${nested ? ' is-nested' : ''}`}>
            <div className="cg-line" onClick={toggleContentModal(row)}>
                {identity.packageType
                    ? <span className={`cg-type cg-type-${identity.packageType}`}>{identity.packageType}</span>
                    : <span className="fas fa-file cg-line-icon text-primary" />}
                <span className="cg-line-title text-truncate" title={identity.contentTitleText}>
                    {identity.contentTitleText || 'Untitled'}
                    {identity.pinned && <span className="ms-2 fas fa-thumbtack text-danger" />}
                    {tone && <span className={`ms-2 fas fa-sm fa-key ${tone}`} />}
                    {deletion && (
                        <span className={`ms-2 badge ${TONE_BADGE_CLASS[deletion.tone]}`}
                              title={[deletion.detail, deletion.nextRetryAt && `Next attempt around ${formatTime(deletion.nextRetryAt)}.`].filter(Boolean).join(' ')}>
                            <span className={`fas ${deletion.icon} me-1`} />{deletion.label}
                        </span>
                    )}
                    {ingestFailed && (
                        <span className="ms-2 badge bg-warning text-dark" title={bacRow.ingestError || 'Ingest failed'}>
                            <span className="fas fa-exclamation-triangle me-1" />Ingest failed
                        </span>
                    )}
                    {ingesting && (
                        <span className="ms-2 badge bg-info text-dark">
                            <span className="fas fa-arrow-up-from-bracket me-1" />Transferring to Mediablock…
                        </span>
                    )}
                </span>
                <span className="cg-line-meta">{formatBytes(identity.dcpSize || row.fileSize || 0)}</span>
                <LocationChips member={identity} toggleContentModal={toggleContentModal} />
            </div>
            {admin && identity.keys.map(keyItem => (
                <KeyLine
                    key={keyItem.uuid}
                    keyItem={keyItem}
                    preferredLocation={preferredLocation}
                    mediablockSerialNumber={mediablockSerialNumber}
                    toggleContentModal={toggleContentModal}
                />
            ))}
        </div>
    );
};

// One card per film: the poster and title come from whichever member resolved a movie, the lines
// below are the DCP families (OV with its VFs nested, keys under the CPL they unlock).
// `preferredLocation` decides which copy a line click opens when a member sits on both.
export const ContentGroupCard = ({ group, preferredLocation = 'mediablock', toggleContentModal, mediablockSerialNumber }) => {
    const user = useSelector(state => state.auth.user);
    const admin = isAdmin(user);
    const lineProps = { preferredLocation, admin, mediablockSerialNumber, toggleContentModal };
    // Zero counts are dropped: a filter can leave a film with keys but no packages.
    const summary = [
        group.counts.packages > 0 && `${group.counts.packages} package${group.counts.packages === 1 ? '' : 's'}`,
        admin && group.counts.keys > 0 && `${group.counts.keys} key${group.counts.keys === 1 ? '' : 's'}`,
    ].filter(Boolean).join(' · ');

    return (
        <div className="content-group bg-broken mb-3">
            {group.posterUrl ? (
                <img className="cg-poster" width={75} height={112} alt={group.title} src={imgUrl(group.posterUrl)} />
            ) : (
                <div className="cg-poster cg-poster-placeholder">
                    <span className="cg-poster-text">{group.title}</span>
                </div>
            )}

            <div className="cg-body">
                <div className="cg-head">
                    <span className="cg-title text-truncate" title={group.title}>{group.title}</span>
                    <span className="cg-summary">{summary}</span>
                </div>

                {group.families.map(family => (
                    <div className="cg-family" key={family.root}>
                        {family.ov && <PackageLine identity={family.ov} {...lineProps} />}
                        {family.ovMissing && (
                            <div className="cg-note">
                                <span className="fas fa-exclamation-triangle me-2" />OV package not on this server
                            </div>
                        )}
                        {family.vfs.map(vf => <PackageLine key={vf.cpl_uuid} identity={vf} nested={!!family.ov} {...lineProps} />)}
                    </div>
                ))}

                {admin && group.orphanKeys.length > 0 && (
                    <div className="cg-family cg-orphans">
                        <div className="cg-section-label">Keys for versions not on this server</div>
                        {group.orphanKeys.map(keyItem => (
                            <KeyLine
                                key={keyItem.uuid}
                                keyItem={keyItem}
                                showTitle
                                preferredLocation={preferredLocation}
                                mediablockSerialNumber={mediablockSerialNumber}
                                toggleContentModal={toggleContentModal}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
